import { LegacyDevice } from 'console/device/device';
import { SimID } from 'console/sim/types';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector, usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { createSubsetSelector } from 'webui/utils/selectors';
import { DevicePermission } from 'console/device/device_ts';
import { Capability } from 'console/device/device-capability';
import { billedSimState, foreignSimState } from 'console/sim/utils/state';
import { getOperatorName, getSimTariffName } from 'console/sim/utils/index';
import { selectDevice, selectDevicesFields } from 'entities/device/lib';
import { removeNulls } from 'utils';
export function getSimInDevice(sim_in_device, billed_sims, foreign_sims) {
    // returns [billed_sim, foreign_sim] pair with at most one element set
    if (billed_sims && sim_in_device?.sim_type === 'billed') {
        const sim_id = sim_in_device.sim_id;
        return [
            billed_sims.filter((sim) => SimID.equals(sim.sim_card.id, sim_id))?.[0] ??
                null,
            null,
        ];
    }
    if (foreign_sims && sim_in_device?.sim_type === 'foreign') {
        const foreignMsisdn = sim_in_device.foreign_msisdn;
        return [
            null,
            foreign_sims.filter((sim) => sim.msisdn === foreignMsisdn)?.[0] ?? null,
        ];
    }
    return [null, null];
}
export function getDeviceIdBySim(devices, sim_in_device) {
    if (sim_in_device.sim_type === 'billed')
        return (devices.find((dev) => dev.sim_in_device?.sim_type === 'billed' &&
            SimID.equals(dev.sim_in_device.sim_id, sim_in_device.sim_id))?.id ?? -1);
    else
        return (devices.find((dev) => dev.sim_in_device?.sim_type === 'foreign' &&
            dev.sim_in_device.foreign_msisdn === sim_in_device.foreign_msisdn)?.id ?? -1);
}
export const selectSimFromDevice = createSelector((state, device_id) => selectDevice(state, device_id)?.sim_in_device, (state) => state.account.sims ?? [], (state) => state.account.foreign_sims ?? [], getSimInDevice);
const selectDeviceForSim = createSelector((state, device_id) => selectDevice(state, device_id), createSubsetSelector(['sim_in_device']));
export function useGetSimInDevice(device_id) {
    const device = usePartialAppSelector(selectDeviceForSim, device_id);
    const billed_sims = useAppSelector((state) => state.account.sims ?? []);
    const foreign_sims = useAppSelector((state) => state.account.foreign_sims ?? []);
    if (device == null) {
        return [null, null];
    }
    return getSimInDevice(device.sim_in_device, billed_sims, foreign_sims);
}
export const select_billed_sim_card = createSelector((state) => state.account, (_, sim_id) => sim_id, (account, sim_id) => {
    if (!sim_id) {
        return null;
    }
    for (const sim of account.sims ?? []) {
        if (SimID.equals(sim.sim_card.id, sim_id)) {
            return sim;
        }
    }
    return null;
});
export const sim_for_device_selector = createSelector((state) => state.account, selectDevicesFields([
    'id',
    'name',
    'capabilities',
    'sim_in_device',
    'individual_device_name',
    'access',
    'user_id',
    'timezone',
]), (account, dev) => dev
    .filter((dev) => LegacyDevice.has_access(account.id, dev, DevicePermission.configure))
    .filter(({ capabilities }) => capabilities.includes(Capability.has_sim_in_device))
    .map((dev) => ({
    ...dev,
    name: LegacyDevice.display_name(dev),
})));
export const sim_prices_selector = createSelector((state) => state.billing, (billing_state) => {
    const sim_prices = new Map((billing_state.prices?.sim_tariffs ?? []).map(({ id, month_price_x100 }) => [
        id,
        month_price_x100,
    ]));
    return sim_prices;
});
export const device_per_sim_selector = createSelector(sim_for_device_selector, (state) => state.account.sims, (state) => state.account.foreign_sims, (devices, billed_sims, foreign_sims) => {
    const dev_per_sim = new Map(removeNulls(devices.map((dev) => {
        const [billed_sim, foreign_sim] = getSimInDevice(dev.sim_in_device, billed_sims, foreign_sims);
        if (billed_sim) {
            return [billed_sim.sim_card.msisdn, dev];
        }
        if (foreign_sim) {
            return [foreign_sim.msisdn, dev];
        }
        return null;
    })));
    return dev_per_sim;
});
export const billed_sim_selector = createSelector((state) => state.account.sims, sim_prices_selector, device_per_sim_selector, (state) => state.billing, (_, descriptions) => descriptions, (billed_sims, sim_prices, dev_per_sim, billing_state, descriptions) => {
    const result_billed_sims = billed_sims.map((sim) => {
        const state = billedSimState(sim, billing_state.settings.data.auto_payment !== null, billing_state.state.data.balance_x100, sim_prices.get(sim.tariff_state.current));
        return {
            msisdn: sim.sim_card.msisdn,
            device_name: dev_per_sim.get(sim.sim_card.msisdn)?.name,
            id: sim.sim_card.id,
            status: state.text,
            warning: state.status === 'warning' || sim.tariff_state.next != null,
            tariff: getSimTariffName(sim.tariff_state.current, descriptions),
            operator: getOperatorName(sim.sim_card.id.operator),
            error: state.status === 'error',
        };
    });
    return result_billed_sims;
});
const device_with_balance = createSelector((state) => state.account, selectDevicesFields(['id', 'balance_value', 'balance']), (account, dev) => new Map(dev.map((dev) => [dev.id, dev])));
export const foreign_sim_selector = createSelector((state) => state.account.foreign_sims, device_per_sim_selector, device_with_balance, (foreign_sims, dev_per_sim, devs_with_balance) => {
    const result_foreign_sims = foreign_sims.map((sim) => {
        const dev = dev_per_sim.get(sim.msisdn);
        let balance = undefined;
        let balance_value = undefined;
        if (dev != null) {
            const dev_with_balance = devs_with_balance.get(dev.id);
            if (dev_with_balance != null) {
                balance = dev_with_balance.balance;
                balance_value = dev_with_balance.balance_value;
            }
        }
        const state = foreignSimState(balance, balance_value);
        return {
            msisdn: sim.msisdn,
            device_name: dev_per_sim.get(sim.msisdn)?.name,
            status: state?.text,
            warning: state.status === 'warning',
        };
    });
    return result_foreign_sims;
});
export const getSimsWithStatus = createSelector(billed_sim_selector, foreign_sim_selector, (billed_sims, foreign_sims) => [...billed_sims, ...foreign_sims]);
export const getBilledSimsCount = createSelector(billed_sim_selector, (billed_sims) => billed_sims.length);
export const getSimLink = createSelector(selectSimFromDevice, ([billed_sim, foreign_sim]) => {
    if (!billed_sim && !foreign_sim) {
        return '';
    }
    if (foreign_sim) {
        return '/sim/list/';
    }
    return `/sim/billed/${billed_sim.sim_card.id.id}/${billed_sim.sim_card.id.operator}`;
});
